import Constants from "utils/Constants";
import Sessions from "utils/Sessions";

export const PDF_SOCKET_TIMEOUT_MS = 180000;
export const PDF_STATUS_POLL_INTERVAL_MS = 1500;
export const PDF_STATUS_POLL_TIMEOUT_MS = 180000;
export const PDF_DOWNLOAD_CLEANUP_DELAY_MS = 1000;
export const EXCEL_STATUS_POLL_INTERVAL_MS = 3000;

/**
 * Delay between each programmatic download trigger (ms).
 *
 * Chrome (and most browsers) throttle rapid programmatic <a>.click()
 * download triggers as a security measure against bulk-download attacks.
 * When multiple PDFs complete around the same time and triggerPdfDownload
 * is called in quick succession, the browser silently blocks all but the
 * first 2-3. Staggering by 800ms per download avoids this throttle.
 */
const DOWNLOAD_STAGGER_MS = 800;

const sleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export const withTimestamp = (url) => `${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}`;

export const fetchBytesFromUrl = async (url, failedMessage = Constants.PDF_FETCH_FAILED) => {
  const response = await fetch(withTimestamp(url), {
    headers: { Authorization: `Bearer ${Sessions.userToken}` },
  });
  if (!response.ok) {
    throw new Error(failedMessage);
  }
  return response.arrayBuffer();
};

export const fetchPdfBytesFromUrl = async (pdfUrl) => fetchBytesFromUrl(pdfUrl);

export const fetchExportStatusPayload = async (statusUrl) => {
  const response = await fetch(withTimestamp(statusUrl), {
    headers: { Authorization: `Bearer ${Sessions.userToken}` },
  });
  if (!response.ok) return null;
  return response.json().catch(() => null);
};

export const pollExportJobStatusUntilReady = async (
  statusUrl,
  {
    failedMessage,
    timeoutMessage = Constants.PDF_EXPORT_TIMED_OUT,
    timeoutMs = PDF_STATUS_POLL_TIMEOUT_MS,
    intervalMs = PDF_STATUS_POLL_INTERVAL_MS,
  } = {}
) => {
  const deadline = Date.now() + timeoutMs;
  const pollOnce = async () => {
    if (Date.now() >= deadline) {
      throw new Error(timeoutMessage);
    }

    const payload = await fetchExportStatusPayload(statusUrl);

    if (payload?.status === "failed") {
      throw new Error(payload?.error || failedMessage);
    }

    if (
      payload &&
      (payload.status === "completed" || payload.status === "emailed") &&
      payload.resultUrl
    ) {
      return payload;
    }

    await sleep(intervalMs);
    return pollOnce();
  };

  return pollOnce();
};

/**
 * Builds the file-service download proxy URL for a given job.
 * This URL streams PDF bytes through the file-service (server-to-S3),
 * bypassing CDN CORS issues that block browser fetch() calls.
 */
export const buildDownloadProxyUrl = (socketUrl, jobId) =>
  `${socketUrl.replace(/\/$/, "")}/api/pdf/pdf-download/${jobId}`;

export const pollPdfJobUntilReady = async (jobId, socketUrl, failedMessage) => {
  const statusUrl = `${socketUrl.replace(/\/$/, "")}/api/pdf/pdf-status/${jobId}`;
  const deadline = Date.now() + PDF_STATUS_POLL_TIMEOUT_MS;
  const pollOnce = async () => {
    if (Date.now() >= deadline) {
      throw new Error(Constants.PDF_EXPORT_TIMED_OUT);
    }

    const response = await fetch(`${statusUrl}?t=${Date.now()}`, {
      redirect: "follow",
      headers: { Authorization: `Bearer ${Sessions.userToken}` },
    });

    if (response.ok) {
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/pdf")) {
        return response.arrayBuffer();
      }

      const payload = await response.json().catch(() => null);
      if (payload?.status === "failed") {
        throw new Error(payload?.error || failedMessage);
      }

      // When the job is completed or emailed, fetch the PDF bytes via the
      // file-service download proxy (CORS-safe) instead of the raw CDN URL.
      if (
        (payload?.status === "completed" || payload?.status === "emailed") &&
        payload?.resultUrl
      ) {
        return fetchPdfBytesFromUrl(buildDownloadProxyUrl(socketUrl, jobId));
      }
    }

    await sleep(PDF_STATUS_POLL_INTERVAL_MS);
    return pollOnce();
  };

  return pollOnce();
};

export const getPdfBytes = (jobId, socketUrl, failedMessage) => async (data) => {
  // downloadUrl is the file-service proxy — requires auth header, safe for CORS.
  if (data?.downloadUrl) {
    return fetchBytesFromUrl(data.downloadUrl);
  }
  // pdfUrl is a raw CDN/S3 URL (legacy file-service versions).
  // Do NOT add Authorization — it triggers a CORS preflight that CDNs reject,
  // and signed CDN URLs carry their own auth in the query string.
  if (data?.pdfUrl) {
    const response = await fetch(withTimestamp(data.pdfUrl));
    if (!response.ok) throw new Error(Constants.PDF_FETCH_FAILED);
    return response.arrayBuffer();
  }
  if (data?.pdfBase64) {
    return Uint8Array.from(atob(data.pdfBase64), (c) => c.charCodeAt(0)).buffer;
  }
  return pollPdfJobUntilReady(jobId, socketUrl, failedMessage);
};

/**
 * Global download queue.
 *
 * Browsers throttle rapid programmatic <a download>.click() calls as a
 * security measure. When 8 PDFs complete around the same time and
 * triggerPdfDownload is called in quick succession, the browser silently
 * blocks all but the first 2-3 downloads.
 *
 * This queue ensures only ONE download is triggered at a time, with an
 * 800ms gap between each, which reliably bypasses the throttle on all
 * major browsers.
 */
let downloadQueue = Promise.resolve();

export const triggerFileDownload = (
  fileBytes,
  filename,
  contentType = "application/octet-stream"
) => {
  // Chain this download onto the global queue so they execute
  // one at a time with a stagger delay between them.
  downloadQueue = downloadQueue
    .catch(() => undefined)
    .then(
      () =>
        new Promise((resolve) => {
          const blob = new Blob([fileBytes], { type: contentType });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();

          setTimeout(() => {
            link.remove();
            window.URL.revokeObjectURL(url);
            resolve();
          }, DOWNLOAD_STAGGER_MS);
        })
    );
  return downloadQueue;
};

export const triggerPdfDownload = (pdfBytes, filename) =>
  triggerFileDownload(pdfBytes, filename, "application/pdf");

export const triggerPdfUrlDownload = (pdfUrl, filename) => {
  downloadQueue = downloadQueue
    .catch(() => undefined)
    .then(
      () =>
        new Promise((resolve) => {
          const url = `${pdfUrl}${pdfUrl.includes("?") ? "&" : "?"}t=${Date.now()}`;

          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();

          setTimeout(() => {
            link.remove();
            resolve();
          }, DOWNLOAD_STAGGER_MS);
        })
    );
  return downloadQueue;
};
